import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Drawer } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useFormik } from "formik";
import moment from "moment";
import CustomButton from "shared/components/customButton";
import Confirmation from "shared/modals/confirmation";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getDemoRequestByIdService,
  activateDemoRequestService,
  reviewDemoRequestService,
  getBookFilterCountsService,
} from "shared/services/adminService";
import { createSchoolPlanSchema } from "shared/utils/validationSchemas";
import baseStyles from "../detail/style.module.scss";
import styles from "./style.module.scss";
import SkeletonLoader from "./skeletonLoader";

const statusConfig: Record<number, { label: string; color: string }> = {
  0: { label: "New", color: "#2196f3" },
  1: { label: "Pending", color: "#ff9800" },
  2: { label: "Approved", color: "#4caf50" },
  3: { label: "Rejected", color: "#f44336" },
  4: { label: "Expired", color: "#9e9e9e" },
  5: { label: "Active", color: "#4caf50" },
  6: { label: "Inactive", color: "#9e9e9e" },
};

interface BookRow {
  id: number;
  // "" means the row is not narrowed by that dimension. Which of the two
  // is used depends on the active tab.
  language_id: string;
  age_group_id: string;
  book_count: string;
  // true once the row was confirmed via the "+" button (or loaded from
  // the backend). Only committed rows are sent.
  committed: boolean;
}

// The five ways an admin can pick books. Exactly one is used per plan —
// switching tabs starts the selection over.
type BookMode = "all" | "count" | "language" | "age" | "both";

const BOOK_MODES: { key: BookMode; label: string; hint: string }[] = [
  {
    key: "all",
    label: "All Books",
    hint: "Give the school the entire library.",
  },
  {
    key: "count",
    label: "Custom Count",
    hint: "Give a number of books taken from anywhere in the library.",
  },
  {
    key: "language",
    label: "By Language",
    hint: "Choose how many books to give from each language.",
  },
  {
    key: "age",
    label: "By Age",
    hint: "Choose how many books to give from each age group.",
  },
  {
    key: "both",
    label: "Language + Age",
    hint: "Choose how many books to give for each language and age combination.",
  },
];

let rowSeq = 0;
const emptyRow = (): BookRow => ({
  id: ++rowSeq,
  language_id: "",
  age_group_id: "",
  book_count: "",
  committed: false,
});

function DemoRequestCreatePlan() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState<any>(null);
  const [pageLoader, setPageLoader] = useState<boolean>(true);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [rejectLoader, setRejectLoader] = useState<boolean>(false);
  // Rows of the active tab. The last row is always the one being filled
  // in; the ones before it were confirmed with the "+" button.
  const [bookRows, setBookRows] = useState<BookRow[]>([emptyRow()]);
  const [bookMode, setBookMode] = useState<BookMode>("both");
  const [booksError, setBooksError] = useState<string>("");
  const [languages, setLanguages] = useState<any[]>([]);
  const [ageGroups, setAgeGroups] = useState<any[]>([]);
  // Raw language + age + books_count combinations from the
  // books/filter-counts endpoint.
  const [bookFilterCounts, setBookFilterCounts] = useState<any[]>([]);
  // Per-language and per-age totals (books available across every age /
  // every language) plus the library-wide total, from the same endpoint.
  const [languageTotals, setLanguageTotals] = useState<any[]>([]);
  const [ageTotals, setAgeTotals] = useState<any[]>([]);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [openNotes, setOpenNotes] = useState<boolean>(false);
  const [notesList, setNotesList] = useState<string[]>([""]);
  const [notesLoader, setNotesLoader] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);

  useBreadcrumbs(
    {
      title: item
        ? `${item.first_name} ${item.last_name}`
        : "Create School Plan",
      path: routeConstant.demoRequestCreatePlan.path.replace(":id", id || ""),
    },
    true,
    item,
  );

  const fetchDetail = () => {
    setPageLoader(true);
    getDemoRequestByIdService(Number(id))
      .then(({ data }) => {
        const record = data?.data;
        setItem(record);

        // Auto-transition status from "New" -> "Pending" on first view
        if (record?.status === 0) {
          const statusForm = new FormData();
          statusForm.append("status", "1");
          reviewDemoRequestService(Number(id), statusForm)
            .then(() => {
              setItem((prev: any) => (prev ? { ...prev, status: 1 } : prev));
            })
            .catch((err) => {
              console.error("Auto status update error:", err);
            });
        }

        // library_books comes back in one of three shapes: "all", a bare
        // count, or an array of scoped selections.
        const books = record?.library_books;
        if (books === "all") {
          setBookMode("all");
          setBookRows([emptyRow()]);
        } else if (
          typeof books === "number" ||
          (typeof books === "string" && /^\d+$/.test(books))
        ) {
          setBookMode("count");
          setBookRows([
            { ...emptyRow(), book_count: String(books), committed: true },
          ]);
        } else if (Array.isArray(books) && books.length > 0) {
          const loaded: BookRow[] = books.map((b: any) => ({
            ...emptyRow(),
            language_id: b?.language_id != null ? String(b.language_id) : "",
            age_group_id: b?.age_range_id != null ? String(b.age_range_id) : "",
            book_count: b?.count != null ? String(b.count) : "",
            committed: true,
          }));
          // Open the tab that matches how the saved plan was built.
          const hasLang = loaded.some((r) => r.language_id);
          const hasAge = loaded.some((r) => r.age_group_id);
          const mode: BookMode =
            hasLang && hasAge
              ? "both"
              : hasLang
                ? "language"
                : hasAge
                  ? "age"
                  : "count";
          setBookMode(mode);
          setBookRows(
            mode === "count" ? loaded.slice(0, 1) : [...loaded, emptyRow()],
          );
        } else {
          setBookRows([emptyRow()]);
        }

        const rawNotes = record?.notes;
        if (rawNotes) {
          try {
            const parsed = JSON.parse(rawNotes);
            setNotesList(
              Array.isArray(parsed) && parsed.length > 0 ? parsed : [rawNotes],
            );
          } catch {
            setNotesList([rawNotes]);
          }
        } else {
          setNotesList([""]);
        }
        setPageLoader(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setPageLoader(false);
      });
  };

  // A single call returns every language + age combination (counts), the
  // per-language and per-age totals, and the library-wide total. The
  // language and age dropdowns are derived from it, so the separate
  // languages / age-ranges calls are no longer needed.
  const fetchDropdowns = () => {
    getBookFilterCountsService()
      .then(({ data }) => {
        const payload = data?.data;
        // The endpoint used to return a bare array of combinations; it now
        // returns { counts, totals, languages, age_ranges }. Both are read
        // so the page keeps working either way.
        const counts: any[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.counts)
            ? payload.counts
            : [];
        const langTotals: any[] = Array.isArray(payload?.languages)
          ? payload.languages
          : [];
        const ageRangeTotals: any[] = Array.isArray(payload?.age_ranges)
          ? payload.age_ranges
          : [];

        setBookFilterCounts(counts);
        setLanguageTotals(langTotals);
        setAgeTotals(ageRangeTotals);
        setGrandTotal(Number(payload?.totals?.books_count) || 0);

        const languageMap = new Map<number, any>();
        const ageMap = new Map<number, any>();
        // Prefer the dedicated totals lists; fall back to the combinations.
        langTotals.forEach((l) => {
          if (l?.language_id != null)
            languageMap.set(l.language_id, {
              id: l.language_id,
              name: l.language_name,
            });
        });
        ageRangeTotals.forEach((a) => {
          if (a?.age_range_id != null)
            ageMap.set(a.age_range_id, {
              id: a.age_range_id,
              text: a.age_range_label,
            });
        });
        counts.forEach((c) => {
          if (c?.language_id != null && !languageMap.has(c.language_id)) {
            languageMap.set(c.language_id, {
              id: c.language_id,
              name: c.language_name,
            });
          }
          if (c?.age_range_id != null && !ageMap.has(c.age_range_id)) {
            ageMap.set(c.age_range_id, {
              id: c.age_range_id,
              text: c.age_range_label,
            });
          }
        });
        setLanguages(Array.from(languageMap.values()));
        setAgeGroups(Array.from(ageMap.values()));
      })
      .catch((err) => console.error("Book filter counts error:", err));
  };

  useEffect(() => {
    fetchDetail();
    fetchDropdowns();
    // eslint-disable-next-line
  }, [id]);

  // ---------------------------------------------------------------------
  // Availability lookups
  //
  // A row can be scoped five different ways. Language and age are both
  // optional ("" = All), so the available-books figure comes from a
  // different bucket of the filter-counts payload for each scope:
  //   1. All books at once      -> handled by the "Select All Books" chip
  //   2. All Languages + All Ages (count typed by hand) -> library total
  //   3. Language only          -> per-language total
  //   4. Age only               -> per-age total
  //   5. Language + Age         -> the language/age combination count
  // ---------------------------------------------------------------------
  const getComboBooks = (languageId: any, ageGroupId: any): number | null => {
    const match = bookFilterCounts.find(
      (c) =>
        String(c.language_id) === String(languageId) &&
        String(c.age_range_id) === String(ageGroupId),
    );
    return match ? Number(match.books_count) : null;
  };

  const getLanguageBooks = (languageId: any): number | null => {
    const match = languageTotals.find(
      (l) => String(l.language_id) === String(languageId),
    );
    if (match) return Number(match.books_count);
    // Fallback: sum every age bucket of that language
    const rows = bookFilterCounts.filter(
      (c) => String(c.language_id) === String(languageId),
    );
    return rows.length
      ? rows.reduce((s, c) => s + (Number(c.books_count) || 0), 0)
      : null;
  };

  const getAgeBooks = (ageGroupId: any): number | null => {
    const match = ageTotals.find(
      (a) => String(a.age_range_id) === String(ageGroupId),
    );
    if (match) return Number(match.books_count);
    const rows = bookFilterCounts.filter(
      (c) => String(c.age_range_id) === String(ageGroupId),
    );
    return rows.length
      ? rows.reduce((s, c) => s + (Number(c.books_count) || 0), 0)
      : null;
  };

  // Books available for a row's scope. Returns null while the
  // filter-counts data has not arrived yet.
  const getAvailableBooks = (
    languageId: string,
    ageGroupId: string,
  ): number | null => {
    if (languageId && ageGroupId) return getComboBooks(languageId, ageGroupId);
    if (languageId) return getLanguageBooks(languageId);
    if (ageGroupId) return getAgeBooks(ageGroupId);
    return grandTotal > 0 ? grandTotal : null;
  };

  // Scope identity ("" = All on either side).
  const scopeKey = (languageId: string, ageGroupId: string) =>
    `${languageId || "all"}-${ageGroupId || "all"}`;

  const DUPLICATE_ERROR = "This selection is already added.";
  const TOTAL_ERROR =
    "The total number of books exceeds what the library has available.";

  // Which selects the active tab uses. A dimension the tab does not use
  // stays "" on every row.
  const usesLanguage = bookMode === "language" || bookMode === "both";
  const usesAge = bookMode === "age" || bookMode === "both";
  // "Custom Count" is a single value, the other tabs are lists of rows.
  const isListMode = usesLanguage || usesAge;

  // Books available for a row under the active tab.
  const rowAvailable = (row: BookRow) =>
    getAvailableBooks(row.language_id, row.age_group_id);

  // The tab's required selects are filled in.
  const rowScopeChosen = (row: BookRow) =>
    (!usesLanguage || Boolean(row.language_id)) &&
    (!usesAge || Boolean(row.age_group_id));

  const rowExceedsMax = (row: BookRow) => {
    if (!row.book_count) return false;
    const available = rowAvailable(row);
    return available != null && Number(row.book_count) > available;
  };

  const isRowComplete = (row: BookRow) =>
    rowScopeChosen(row) && Number(row.book_count) > 0 && !rowExceedsMax(row);

  // Returns the first row repeating an earlier row's scope.
  const findDuplicateRow = (rows: BookRow[]) => {
    const seen = new Set<string>();
    for (const r of rows) {
      if (!rowScopeChosen(r)) continue;
      const key = scopeKey(r.language_id, r.age_group_id);
      if (seen.has(key)) return r;
      seen.add(key);
    }
    return null;
  };

  // The last row is the one being filled in. It counts as soon as it is
  // complete — pressing "+" only opens another row, so a filled-in row is
  // never silently dropped from the plan.
  const draftRow = bookRows[bookRows.length - 1];
  const countedRows = isListMode
    ? bookRows.filter(
        (r) => r.committed || (r.id === draftRow?.id && isRowComplete(r)),
      )
    : // Custom Count is a single value with no "+" button.
      bookRows.slice(0, 1);
  const rowsTotal = countedRows.reduce(
    (sum, r) => sum + (Number(r.book_count) || 0),
    0,
  );
  // "All Books" grants the whole library in one go.
  const totalBooks = bookMode === "all" ? grandTotal : rowsTotal;
  const remainingBooks = Math.max(grandTotal - totalBooks, 0);
  const exceedsGrandTotal = grandTotal > 0 && totalBooks > grandTotal;

  const duplicateRow = findDuplicateRow(bookRows);
  // True when another row already uses this exact scope — used to grey
  // out options that would only produce a duplicate.
  const isScopeTaken = (
    rowId: number,
    languageId: string,
    ageGroupId: string,
  ) =>
    bookRows.some(
      (r) =>
        r.id !== rowId &&
        rowScopeChosen(r) &&
        scopeKey(r.language_id, r.age_group_id) ===
          scopeKey(languageId, ageGroupId),
    );

  // What is wrong with the row in progress, or "".
  const draftError = (() => {
    if (!isListMode || !draftRow || !rowScopeChosen(draftRow)) return "";
    if (duplicateRow?.id === draftRow.id) return DUPLICATE_ERROR;
    if (!draftRow.book_count) return "";
    const available = rowAvailable(draftRow);
    if (available != null && Number(draftRow.book_count) > available)
      return `Only ${available} books are available for this selection.`;
    if (exceedsGrandTotal) return TOTAL_ERROR;
    return "";
  })();

  const canAddRow =
    isListMode && draftRow && isRowComplete(draftRow) && !draftError;

  const addRow = () => {
    if (!canAddRow) return;
    setBookRows((prev) => [
      ...prev.map((r, i) =>
        i === prev.length - 1 ? { ...r, committed: true } : r,
      ),
      emptyRow(),
    ]);
    setBooksError("");
  };

  const deleteRow = (rowId: number) => {
    setBookRows((prev) => prev.filter((r) => r.id !== rowId));
    setBooksError("");
  };

  const updateRow = (rowId: number, field: keyof BookRow, value: string) => {
    setBookRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const updated = { ...r, [field]: value };
        if (field === "book_count") {
          // Never let a row ask for more than its scope can give.
          const available = getAvailableBooks(
            updated.language_id,
            updated.age_group_id,
          );
          let val = value.replace(/[^0-9]/g, "");
          if (val === "0") val = "1";
          if (available != null && val !== "" && Number(val) > available) {
            val = String(available);
          }
          updated.book_count = val;
        } else {
          // The available books change with the scope, so a previously
          // typed count may no longer be valid.
          updated.book_count = "";
        }
        return updated;
      }),
    );
    setBooksError("");
  };

  // Switching tab switches the method — the previous tab's rows are
  // dropped so only one method is ever sent.
  const switchMode = (mode: BookMode) => {
    if (mode === bookMode) return;
    setBookMode(mode);
    setBookRows([emptyRow()]);
    setBooksError("");
  };

  const activeMode =
    BOOK_MODES.find((m) => m.key === bookMode) ?? BOOK_MODES[0];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      students_count: item?.students_count ?? "",
      teachers_count: item?.teachers_count ?? "",
      is_multi_campus: item?.is_multi_campus ? "1" : "0",
      duration: item?.duration ?? 30,
      campuses_count: item?.campuses_count ?? "",
    },
    validationSchema: createSchoolPlanSchema,
    onSubmit: (values) => {
      // library_books goes out in one of three shapes:
      //   "all"                                    -> the whole library
      //   250                                      -> a bare count
      //   [{ language_id?, age_range_id?, count }] -> scoped selections
      // The key for a dimension the active tab does not use is omitted
      // entirely (not sent as null).
      type BooksPayload =
        | "all"
        | number
        | { language_id?: number; age_range_id?: number; count: number }[];
      let libraryBooks: BooksPayload;

      if (bookMode === "all") {
        if (grandTotal <= 0) {
          setBooksError("No books are available in the library right now.");
          return;
        }
        libraryBooks = "all";
      } else if (!isListMode) {
        // Custom Count -> a bare number.
        if (rowsTotal <= 0) {
          setBooksError("Please enter the number of books.");
          return;
        }
        if (exceedsGrandTotal) {
          setBooksError(TOTAL_ERROR);
          return;
        }
        libraryBooks = rowsTotal;
      } else {
        if (countedRows.length === 0) {
          setBooksError("Please add at least one selection using the + button.");
          return;
        }
        if (!countedRows.every(isRowComplete)) {
          setBooksError("Please complete every selection.");
          return;
        }
        if (findDuplicateRow(countedRows)) {
          setBooksError(DUPLICATE_ERROR);
          return;
        }
        if (exceedsGrandTotal) {
          setBooksError(TOTAL_ERROR);
          return;
        }
        libraryBooks = countedRows.map((r) => ({
          ...(r.language_id ? { language_id: Number(r.language_id) } : {}),
          ...(r.age_group_id ? { age_range_id: Number(r.age_group_id) } : {}),
          count: Number(r.book_count),
        }));
      }

      // Sent as JSON, not FormData — library_books has to keep its type.
      const payload: Record<string, any> = {
        students_count: Number(values.students_count),
        teachers_count: Number(values.teachers_count),
        is_multi_campus: values.is_multi_campus === "1" ? 1 : 0,
        demo_duration_days: Number(values.duration),
        library_books: libraryBooks,
      };
      if (values.is_multi_campus === "1") {
        payload.campuses_count = Number(values.campuses_count);
      }

      setSubmitLoader(true);
      activateDemoRequestService(Number(id), payload)
        .then(({ data }) => {
          toastMessage("success", data?.message);
          setSubmitLoader(false);
          formik.resetForm();
          navigate(`${routeConstant.dashboard.path}?tab=demo-requests`);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          setSubmitLoader(false);
        });
    },
  });

  const handleNotesSubmit = () => {
    const filtered = notesList.filter((n) => n.trim() !== "");
    const notesJson = JSON.stringify(filtered);
    const formData = new FormData();
    formData.append("notes", notesJson);
    setNotesLoader(true);
    reviewDemoRequestService(Number(id), formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setNotesLoader(false);
        setItem((prev: any) => ({ ...prev, notes: notesJson }));
        setNotesList(filtered.length > 0 ? filtered : [""]);
        setOpenNotes(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setNotesLoader(false);
      });
  };

  const handleReject = (reason: string) => {
    if (!reason?.trim()) return;
    const formData = new FormData();
    formData.append("status", "3");
    formData.append("rejection_reason", reason);
    setRejectLoader(true);
    reviewDemoRequestService(Number(id), formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setRejectLoader(false);
        setOpenRejectModal(false);
        fetchDetail();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setRejectLoader(false);
      });
  };

  const isMultiCampus = formik.values.is_multi_campus === "1";
  // Create Plan needs a usable selection from the active tab only.
  const disableCreatePlan = (() => {
    if (bookMode === "all") return grandTotal <= 0;
    if (exceedsGrandTotal) return true;
    if (!isListMode) return rowsTotal <= 0;
    return (
      countedRows.length === 0 ||
      !countedRows.every(isRowComplete) ||
      Boolean(findDuplicateRow(countedRows))
    );
  })();
  const status = statusConfig[item?.status];
  const hasNotes = (() => {
    if (!item?.notes) return false;
    try {
      const parsed = JSON.parse(item.notes);
      return Array.isArray(parsed) && parsed.some((n: string) => n.trim());
    } catch {
      return Boolean(item.notes);
    }
  })();

  if (pageLoader) {
    return <SkeletonLoader />;
  }

  return (
    <div>
      {/* Info Section */}
      <Box className={baseStyles.infoCard}>
        <div className={baseStyles.infoTop}>
          <div className={baseStyles.infoLeft}>
            <div className={baseStyles.avatarCircle}>
              <PersonIcon />
            </div>
            <div className={baseStyles.infoMeta}>
              <div className={baseStyles.name}>
                {item?.first_name} {item?.last_name}
              </div>
              <div className={baseStyles.metaRow}>
                <div className={baseStyles.metaCol}>
                  <span className={baseStyles.metaLabel}>School</span>
                  <span className={baseStyles.metaValue}>
                    {item?.school_name || "—"}
                  </span>
                </div>
                <div className={baseStyles.metaCol}>
                  <span className={baseStyles.metaLabel}>Role</span>
                  <span className={baseStyles.metaValue}>
                    {item?.role || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={baseStyles.infoActions}>
            <button
              className={baseStyles.notesBtn}
              onClick={() => setOpenNotes(true)}
            >
              {hasNotes ? "Edit Notes" : "Add Notes"}
            </button>
            {status && (
              <span
                className={baseStyles.statusChip}
                style={{ backgroundColor: status.color }}
              >
                {status.label}
              </span>
            )}
          </div>
        </div>

        <hr className={baseStyles.divider} />

        <div className={baseStyles.infoIconRow}>
          <div className={baseStyles.infoIconItem}>
            <PhoneIcon className={baseStyles.infoIcon} />
            <span>{item?.phone || "—"}</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <EmailIcon className={baseStyles.infoIcon} />
            <span style={{wordBreak:'break-word'}}>{item?.email || "—"}</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <GroupsIcon className={baseStyles.infoIcon} />
            <span>{item?.students_count ?? "—"} students</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <PersonOutlineIcon className={baseStyles.infoIcon} />
            <span>{item?.teachers_count ?? "—"} teachers</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <CalendarTodayIcon className={baseStyles.infoIcon} />
            <span>
              {item?.created_at
                ? moment(item.created_at).format("MMM DD, YYYY")
                : "—"}
            </span>
          </div>
        </div>
      </Box>

      {/* Plan Form Section */}
      <Box className={baseStyles.formCard}>
        <div className={baseStyles.formTitle}>Create School Plan</div>
        <form onSubmit={formik.handleSubmit}>
          <div className={baseStyles.twoColRow}>
            <div className={baseStyles.fieldWrapper}>
              <label className={baseStyles.label}>Students Count</label>
              <input
                type="number"
                className={baseStyles.inputField}
                id="students_count"
                name="students_count"
                value={formik.values.students_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={0}
              />
              {formik.touched.students_count &&
                formik.errors.students_count && (
                  <div className={baseStyles.errorText}>
                    {formik.errors.students_count as string}
                  </div>
                )}
            </div>

            <div className={baseStyles.fieldWrapper}>
              <label className={baseStyles.label}>Teachers Count</label>
              <input
                type="number"
                className={baseStyles.inputField}
                id="teachers_count"
                name="teachers_count"
                value={formik.values.teachers_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={0}
              />
              {formik.touched.teachers_count &&
                formik.errors.teachers_count && (
                  <div className={baseStyles.errorText}>
                    {formik.errors.teachers_count as string}
                  </div>
                )}
            </div>
          </div>

          <div className={baseStyles.twoColRow}>
            <div className={baseStyles.fieldWrapper}>
              <label className={baseStyles.label}>Multi Campus</label>
              <select
                className={baseStyles.selectField}
                id="is_multi_campus"
                name="is_multi_campus"
                value={formik.values.is_multi_campus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
              {formik.touched.is_multi_campus &&
                formik.errors.is_multi_campus && (
                  <div className={baseStyles.errorText}>
                    {formik.errors.is_multi_campus as string}
                  </div>
                )}
            </div>

            <div className={baseStyles.fieldWrapper}>
              <label className={baseStyles.label}>Duration (days)</label>
              <input
                type="number"
                className={baseStyles.inputField}
                id="duration"
                name="duration"
                value={formik.values.duration}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={1}
                max={30}
              />
              {formik.touched.duration && formik.errors.duration && (
                <div className={baseStyles.errorText}>
                  {formik.errors.duration as string}
                </div>
              )}
            </div>
          </div>

          {isMultiCampus && (
            <div className={baseStyles.fieldWrapper}>
              <label className={baseStyles.label}>No. of Campuses</label>
              <input
                type="number"
                className={baseStyles.inputField}
                id="campuses_count"
                name="campuses_count"
                value={formik.values.campuses_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={1}
              />
              {formik.touched.campuses_count &&
                formik.errors.campuses_count && (
                  <div className={baseStyles.errorText}>
                    {formik.errors.campuses_count as string}
                  </div>
                )}
            </div>
          )}

          {/* Books Section */}
          <Box className={styles.booksSection}>
            <div className={styles.booksHeader}>
              <div className={styles.sectionTitle}>Books</div>
              <div className={styles.libraryTotal}>
                <LibraryBooksIcon fontSize="small" />
                {grandTotal} books in library
              </div>
            </div>

            {/* One tab per way of picking books */}
            <div className={styles.bookTabs} role="tablist">
              {BOOK_MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={bookMode === m.key}
                  className={`${styles.bookTab} ${
                    bookMode === m.key ? styles.bookTabActive : ""
                  }`}
                  onClick={() => switchMode(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div className={styles.modeHint}>{activeMode.hint}</div>

            {booksError && (
              <div className={styles.booksError}>{booksError}</div>
            )}

            {bookMode === "all" && (
              <div className={styles.allBooksCard}>
                <CheckCircleIcon className={styles.allBooksCardIcon} />
                <div>
                  <div className={styles.allBooksCardTitle}>
                    All {grandTotal} books included
                  </div>
                  <div className={styles.allBooksCardSub}>
                    Every language and every age group in the library.
                  </div>
                </div>
              </div>
            )}

            {bookMode === "count" && bookRows[0] && (
              <div className={styles.bookRow}>
                <div className={styles.bookField}>
                  <label className={styles.bookLabel}>No. of Books</label>
                  <div className={styles.bookInputWrapper}>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`${styles.bookInput} ${styles.bookInputWithBadge}`}
                      placeholder="0"
                      value={bookRows[0].book_count}
                      onChange={(e) =>
                        updateRow(bookRows[0].id, "book_count", e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className={styles.bookMaxBtn}
                      disabled={grandTotal <= 0}
                      title="Use every book in the library"
                      onClick={() =>
                        updateRow(
                          bookRows[0].id,
                          "book_count",
                          String(grandTotal),
                        )
                      }
                    >
                      Max {grandTotal}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isListMode &&
              bookRows.map((row, index) => {
                const isDraft = index === bookRows.length - 1;
                const available = rowAvailable(row);
                const scopeChosen = rowScopeChosen(row);
                const showMax = scopeChosen && available != null;
                const isDuplicate = duplicateRow?.id === row.id;
                return (
                  <div key={row.id} className={styles.bookRow}>
                    {usesLanguage && (
                      <div className={styles.bookField}>
                        {index === 0 && (
                          <label className={styles.bookLabel}>Language</label>
                        )}
                        <select
                          className={`${styles.bookSelect} ${
                            isDuplicate ? styles.errorBorder : ""
                          }`}
                          value={row.language_id}
                          onChange={(e) =>
                            updateRow(row.id, "language_id", e.target.value)
                          }
                        >
                          <option value="">Select Language</option>
                          {languages.map((l) => {
                            // Books this language holds within the age group
                            // already chosen on this row (if the tab uses one).
                            // Only used to grey out empty options — the
                            // number itself is shown by the Max chip.
                            const n =
                              usesAge && row.age_group_id
                                ? getComboBooks(l.id, row.age_group_id)
                                : getLanguageBooks(l.id);
                            return (
                              <option
                                key={l.id}
                                value={l.id}
                                disabled={
                                  n === 0 ||
                                  isScopeTaken(
                                    row.id,
                                    String(l.id),
                                    row.age_group_id,
                                  )
                                }
                              >
                                {l.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                    {usesAge && (
                      <div className={styles.bookField}>
                        {index === 0 && (
                          <label className={styles.bookLabel}>Age Group</label>
                        )}
                        <select
                          className={`${styles.bookSelect} ${
                            isDuplicate ? styles.errorBorder : ""
                          }`}
                          value={row.age_group_id}
                          onChange={(e) =>
                            updateRow(row.id, "age_group_id", e.target.value)
                          }
                        >
                          <option value="">Select Age Group</option>
                          {ageGroups.map((a) => {
                            const n =
                              usesLanguage && row.language_id
                                ? getComboBooks(row.language_id, a.id)
                                : getAgeBooks(a.id);
                            return (
                              <option
                                key={a.id}
                                value={a.id}
                                disabled={
                                  n === 0 ||
                                  isScopeTaken(
                                    row.id,
                                    row.language_id,
                                    String(a.id),
                                  )
                                }
                              >
                                {a.text}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                    <div className={styles.bookField}>
                      {index === 0 && (
                        <label className={styles.bookLabel}>No. of Books</label>
                      )}
                      <div className={styles.bookInputWrapper}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`${styles.bookInput} ${
                            rowExceedsMax(row) ? styles.errorBorder : ""
                          } ${showMax ? styles.bookInputWithBadge : ""}`}
                          placeholder={
                            scopeChosen && available === 0
                              ? "None available"
                              : "0"
                          }
                          disabled={!scopeChosen || available === 0}
                          value={row.book_count}
                          onChange={(e) =>
                            updateRow(row.id, "book_count", e.target.value)
                          }
                        />
                        {showMax && (
                          <button
                            type="button"
                            className={styles.bookMaxBtn}
                            disabled={available === 0}
                            title="Use all available books"
                            onClick={() =>
                              updateRow(row.id, "book_count", String(available))
                            }
                          >
                            Max {available}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookActions}>
                      {isDraft ? (
                        <button
                          type="button"
                          className={styles.addRowBtn}
                          disabled={!canAddRow}
                          onClick={addRow}
                          title="Add another selection"
                        >
                          <AddIcon fontSize="small" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={styles.deleteRowBtn}
                          onClick={() => deleteRow(row.id)}
                          title="Remove selection"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

            {draftError && (
              <div className={styles.fieldError}>{draftError}</div>
            )}

            {/* Running total against the library */}
            <div className={styles.totalCard}>
              <div className={styles.totalTop}>
                <span className={styles.totalLabel}>Total Books</span>
                <span
                  className={`${styles.totalValue} ${
                    exceedsGrandTotal ? styles.totalValueError : ""
                  }`}
                >
                  <strong>{totalBooks}</strong>
                  {grandTotal > 0 && (
                    <span className={styles.totalOf}>/ {grandTotal}</span>
                  )}
                </span>
              </div>
              {grandTotal > 0 && (
                <div className={styles.totalBar}>
                  <div
                    className={`${styles.totalBarFill} ${
                      exceedsGrandTotal ? styles.totalBarFillError : ""
                    }`}
                    style={{
                      width: `${Math.min((totalBooks / grandTotal) * 100, 100)}%`,
                    }}
                  />
                </div>
              )}
              {exceedsGrandTotal ? (
                <div className={styles.fieldError}>{TOTAL_ERROR}</div>
              ) : bookMode !== "all" && totalBooks === 0 ? (
                // Explains why Create Plan is greyed out.
                <div className={styles.totalNote}>
                  {isListMode
                    ? "Choose a selection and a number of books to continue."
                    : "Enter a number of books to continue."}
                </div>
              ) : (
                grandTotal > 0 &&
                bookMode !== "all" && (
                  <div className={styles.totalNote}>
                    {remainingBooks} books still available
                  </div>
                )
              )}
            </div>
          </Box>

          <div className={baseStyles.btnContainer}>
            <CustomButton
              text="Reject"
              type="button"
              buttonStyle={baseStyles.btnDanger}
              handleClick={() => setOpenRejectModal(true)}
            />
            <CustomButton
              text="Create Plan"
              buttonStyle={baseStyles.btnCustom}
              handleClick={formik.handleSubmit}
              disabled={disableCreatePlan}
            />
          </div>
        </form>
      </Box>

      {/* Reject Modal */}
      <Confirmation
        open={openRejectModal}
        handleClose={() => setOpenRejectModal(false)}
        handleClick={handleReject}
        loader={rejectLoader}
        text={{ label: "Are you sure you want to reject this demo request?" }}
        showTextArea={true}
      />

      {/* Notes Drawer */}
      <Drawer
        sx={{
          "& .MuiDrawer-paper": { width: 320 },
        }}
        anchor="right"
        open={openNotes}
        onClose={() => setOpenNotes(false)}
      >
        <div className={baseStyles.drawerContainer}>
          <div className={baseStyles.drawerHeader}>
            <span className={baseStyles.drawerTitle}>Notes</span>
            <button
              className={baseStyles.drawerClose}
              onClick={() => setOpenNotes(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={baseStyles.drawerBody}>
            {notesList.map((note, index) => (
              <div key={index} className={baseStyles.noteItem}>
                <div className={baseStyles.noteLabelRow}>
                  <label className={baseStyles.label}>Note {index + 1}</label>
                  {notesList.length > 1 && (
                    <button
                      className={baseStyles.removeNoteBtn}
                      onClick={() =>
                        setNotesList((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
                <textarea
                  className={baseStyles.textarea}
                  value={note}
                  onChange={(e) => {
                    const updated = [...notesList];
                    updated[index] = e.target.value;
                    setNotesList(updated);
                  }}
                  placeholder="Add note here..."
                />
              </div>
            ))}
            <button
              className={baseStyles.addNoteBtn}
              onClick={() => setNotesList((prev) => [...prev, ""])}
            >
              <AddIcon fontSize="small" /> Add Note
            </button>
          </div>

          <div className={baseStyles.drawerFooter}>
            <CustomButton
              text="Submit"
              buttonStyle={baseStyles.btnCustom}
              handleClick={handleNotesSubmit}
            />
            <CustomButton
              text="Cancel"
              buttonStyle={baseStyles.btnInsideOut}
              handleClick={() => setOpenNotes(false)}
            />
          </div>
        </div>
      </Drawer>

      {(submitLoader || notesLoader) && <Loader />}
    </div>
  );
}

export default DemoRequestCreatePlan;
